import {
  getDeviceIcon,
  getObjectDescLabel,
  getObjectIconFromType,
  getExtDeviceIdentifier,
  getIconFromProductType,
  getExtDeviceActiveLink,
  getExtDeviceIconFromType,
  getConfiguredChannelsOrModulesSettings,
  hasDeviceSettingsButtonVisible,
  hasDeviceCommandIconButtonVisible,
  getThermo99Description,
} from '@/core/utils/utils';
import { PropType } from 'vue';
import { Vue, Options } from 'vue-class-component';
import {
  RoomDTO,
  DeviceDTO,
  ComponentType,
  RoomObjectDTO,
  RoomObjectType,
  DefaultDevicesSettingsDTO,
} from '@/../client-generator/generated-clients/plants';
import DialogCommandIcon from './DialogCommandIcon/DialogCommandIcon.vue';
import DialogObjectSettings from './DialogObjectSettings/DialogObjectSettings.vue';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import DialogThermoObjectSettings from './DialogThermoObjectSettings/DialogThermoObjectSettings.vue';
import DeviceCarousel from '@/views/components/DeviceCarousel/DeviceCarousel.vue';

@Options({
  name: 'RoomObjectGroup',
  props: {
    roomObject: {
      type: Object as PropType<RoomObjectDTO>,
      required: true,
    },
    room: {
      type: Object as PropType<RoomDTO>,
      required: true,
    },
    hideConfig: {
      type: Boolean,
      default: false,
    },
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DialogConfirmDelete,
    DialogCommandIcon,
    DialogObjectSettings,
    DialogThermoObjectSettings,
    DeviceCarousel,
  },
  emits: ['delete-object'],
})
export default class RoomObjectGroup extends Vue {
  room!: RoomDTO;
  roomObject!: RoomObjectDTO;
  hideConfig!: boolean;
  disableActions!: boolean;
  getDeviceIcon = getDeviceIcon;
  getExtDeviceIconFromType = getExtDeviceIconFromType;
  getExtDeviceIdentifier = getExtDeviceIdentifier;
  getExtDeviceActiveLink = getExtDeviceActiveLink;
  RoomObjectType = RoomObjectType;
  getObjectDescLabel = getObjectDescLabel;
  getConfiguredChannelsOrModulesSettings = getConfiguredChannelsOrModulesSettings;
  getThermo99Description = getThermo99Description;

  isVisibleDialogCommandIcon = false;
  isVisibleDialogObjectSettings = false;
  isVisibleDialogThermoObjectSettings = false;
  isVisibleDeleteObjectConfirm = false;
  selectedDefaultSettings: DefaultDevicesSettingsDTO | null = null;


  get isThermoRoomObject() {
    return this.roomObject.type === RoomObjectType.Thermostat;
  }

  goToAssignObject() {
    this.$router.push({
      name:
        this.roomObject.type !== RoomObjectType.Thermostat
          ? 'page-assign-room-object'
          : 'page-assign-thermo-room-object',
      params: {
        roomObjectId: this.roomObject.id ?? -1,
      },
      query: {
        roomType: this.room.type,
        roomName: this.room.name,
      },
    });
  }

  get roomObjectDevices() {
    const devices = this.roomObject.devices;
    if (!devices) return [];
    const actuators: DeviceDTO[] = [];
    const commands: DeviceDTO[] = [];
    const actuatorAndCommand: DeviceDTO[] = [];
    for (const device of devices) {
      if (device.isAssignedActuator) {
        actuators.push(device);
      }
      else if (device.isAssignedCommand) {
        commands.push(device);
      }
      if (device.isAssignedActuatorAndCommand) {
        const deviceToClone = { ...device } as DeviceDTO;
        deviceToClone.selectedComponentType = ComponentType.Command;
        actuatorAndCommand.push(deviceToClone);
      }
    }
    return [...actuators, ...actuatorAndCommand, ...commands];
  }

  getConfiguredChannelsOrModulesText(device: DeviceDTO) {
    var activeLinksLabel = getConfiguredChannelsOrModulesSettings(device)?.activeLinksLabel;

    if (!activeLinksLabel) return activeLinksLabel;

    // Replace hardcoded "Channel" and "Module" with translated versions
    let translatedLabel = activeLinksLabel;
    
    if (translatedLabel.includes('Channel ')) {
      translatedLabel = translatedLabel.replace(/Channel /g, this.$t('AppResource.Channel') + ' ');
    }
    
    if (translatedLabel.includes('Module ')) {
      translatedLabel = translatedLabel.replace(/Module /g, this.$t('AppResource.Module') + ' ');
    }

    return translatedLabel;
  }

  getObjectIcon(roomObject: RoomObjectDTO) {
    if (roomObject.type === undefined) return 'icon-general-object';
    if (roomObject.type === RoomObjectType.ActuactorLoadControl) {
      return getIconFromProductType(roomObject.energyMeterAndLoadControlType);
    }
    return getObjectIconFromType(roomObject.type);
  }
  
  toggleDeleteObjectConfirm() {
    this.isVisibleDeleteObjectConfirm = !this.isVisibleDeleteObjectConfirm;
  }
  
  async handleDeleteObject() {
    this.$emit('delete-object');
  }
  
  hasDeviceSettingsButton(device: DeviceDTO) {
    const deviceSettings = getConfiguredChannelsOrModulesSettings(device);
    return deviceSettings ? hasDeviceSettingsButtonVisible(deviceSettings) : false;
  }

  hasDeviceCommandIconButton(device: DeviceDTO) {
    const deviceSettings = getConfiguredChannelsOrModulesSettings(device);
    return deviceSettings ? hasDeviceCommandIconButtonVisible(deviceSettings) : false;
  }
  
  toggleDialogCommandIcon(device: DeviceDTO) {
    const defaultSettings = getConfiguredChannelsOrModulesSettings(device) ?? null;
    this.isVisibleDialogCommandIcon = !this.isVisibleDialogCommandIcon;
    this.selectedDefaultSettings = defaultSettings;
  }

  toggleDialogObjectSettings(device: DeviceDTO) {
    const defaultSettings = getConfiguredChannelsOrModulesSettings(device) ?? null;
    this.isVisibleDialogObjectSettings = !this.isVisibleDialogObjectSettings;
    this.selectedDefaultSettings = defaultSettings;
  }

  toggleDialogThermoObjectSettings() {
    this.isVisibleDialogThermoObjectSettings = !this
      .isVisibleDialogThermoObjectSettings;
  }
}
